import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import * as Highcharts from 'highcharts';
import { ChartData, UserDashboardService, formatDashboardUserEmail } from '../../../services/user-dashboard.service';
import { LargeChartViewService } from '../../../services/large-chart-view.service';
import { formatDateTimeInTimezone, formatUserFriendlyDateTimeInTimezone } from '../../../shared/chart-timezone.util';
import {
  normalizeCategoryArray,
  normalizeChartDisplayLabel,
  normalizeHighchartsApiConfig
} from '../../../shared/chart-label.util';
import {
  buildHighchartsGetSvgExportOptions,
  fitChartExportCanvas
} from '../../../shared/chart-image-export.util';
import { downloadChartTableCsv } from '../../../shared/chart-table-csv-export.util';
import { SENTIMENT_SANKEY_COLORS } from '../../../shared/constant';
import { ConfirmPopupService } from '../../../services/confirm-popup.service';
import { NotificationService } from '../../../services/notification.service';

// Import Highcharts modules for special chart types
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import HighchartsTreemap from 'highcharts/modules/treemap';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsExportData from 'highcharts/modules/export-data';

// Initialize modules
HighchartsSankey(Highcharts);
HighchartsHeatmap(Highcharts);
HighchartsTreemap(Highcharts);
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsExportData(Highcharts);

// Set global Highcharts options to disable thousand separators
Highcharts.setOptions({
  lang: {
    thousandsSep: ''
  },
  tooltip: {
    pointFormat: '{point.name}: {point.y}'
  }
});

@Component({
  selector: 'app-chart-overview',
  standalone: true,
  imports: [CommonModule, HighchartsChartModule, MatTooltipModule],
  templateUrl: './chart-overview.component.html',
  styleUrl: './chart-overview.component.scss'
})
export class ChartOverviewComponent implements OnInit, OnChanges {
  @Input() chartData!: ChartData;
  @Input() chartIndex?: number; // Index of chart in dashboard for delete/edit operations
  @Input() isEditMode: boolean = false; // Track if dashboard is in edit mode
  @Input() hasUnsavedChanges: boolean = false; // Track if dashboard has unsaved changes
  @Output() editRequested = new EventEmitter<void>();
  @Output() deleteRequested = new EventEmitter<void>();
  @Output() downloadRequested = new EventEmitter<void>();
  @Output() saveAndLargeViewRequested = new EventEmitter<void>();

  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartRef: any;
  isLoading = false;
  hasError = false;
  errorMessage = '';
  showMenu = false; // Track menu visibility
  isLargeViewHovered = false; // Track large view button hover state
  isMoreHovered = false; // Track more button hover state

  constructor(
    private userDashboardService: UserDashboardService,
    private router: Router,
    private largeChartViewService: LargeChartViewService,
    private confirmPopupService: ConfirmPopupService,
    private notificationService: NotificationService
  ) {}

  /**
   * Extract chart data from either API response format (delegates to service).
   * Used when processing a raw API response; when chartData is from parent it is already normalized.
   */
  extractChartData(response: any): any {
    return this.userDashboardService.extractChartData(response);
  }

  ngOnInit() {
    if (this.chartData) {
      this.updateChart();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData'] && this.chartData) {
      this.updateChart();
    }
  }

  updateChart() {
    if (!this.chartData || !this.chartData.data) {
      this.hasError = true;
      this.errorMessage = 'No chart data available';
      return;
    }

    // Check if no_data_found flag is set to true
    if (this.chartData.no_data_found === true) {
      this.hasError = true;
      this.errorMessage = 'No data found for the selected date range and filters';
      this.chartOptions = {};
      return;
    }

    try {
      this.hasError = false;
      this.errorMessage = '';

      const chartType = this.chartData.chart_type.toLowerCase();

      // If chart_config is available from API, use it directly (it's ready to use)
      // But for line/area charts from dashboard, we need to use normalized data instead
      const isLineOrAreaChart = ['line', 'spline', 'area', 'areaspline'].includes(chartType);
      const hasValidConfig = this.chartData.config && this.chartData.config.chart && this.chartData.config.series;

      if (hasValidConfig && !isLineOrAreaChart) {
        // Use subtitle from API as-is (no client-side formatting)
        const subtitleText = this.chartData.config.subtitle?.text ?? '';

        // Use chart_config from API, but ensure title is empty (shown in HTML header)
        // and format tooltip Y values (integers for volume/count, 2 decimals for percentage)
        const apiConfig = normalizeHighchartsApiConfig(
          { ...this.chartData.config },
          this.chartData.chart_type
        );

        // For pie/donut charts, add "Remaining Categories" if total is less than 100%
        if (apiConfig.series && apiConfig.series.length > 0) {
          const chartType = apiConfig.chart?.type || 'column';
          const isPieOrDonut = chartType === 'pie' ||
                               (chartType === 'pie' && apiConfig.plotOptions?.pie?.innerSize) ||
                               this.chartData.chart_type?.toLowerCase() === 'donut' ||
                               this.chartData.chart_type?.toLowerCase() === 'pie';

          if (isPieOrDonut) {
            const seriesData = apiConfig.series[0].data || [];
            const total = seriesData.reduce((sum: number, item: any) => {
              const value = typeof item === 'object' ? item.y : item;
              return sum + (parseFloat(value) || 0);
            }, 0);

            // If total is less than 99.5% (allowing for rounding), add remaining categories
            if (total < 99.5) {
              const remaining = 100 - total;
              apiConfig.series[0].data = [
                ...seriesData,
                {
                  name: 'Remaining Categories *',
                  y: parseFloat(remaining.toFixed(2)),
                  // Keep slice for correct pie proportions; do not surface as a normal category in the UI
                  showInLegend: false,
                  dataLabels: { enabled: false },
                  enableMouseTracking: false
                }
              ];
            }
          }
        }

        // For donut/pie charts with percentage data, prevent Highcharts from recalculating percentages
        if (apiConfig.plotOptions && apiConfig.plotOptions.pie) {
          apiConfig.plotOptions.pie.dataLabels = {
            ...apiConfig.plotOptions.pie.dataLabels,
            format: '{point.name}: {point.y:.2f}%'
          };
        }

        // Align tooltip Y formatting with metric (volume → whole numbers; percentage → 2 decimals)
        if (apiConfig.tooltip) {
          if (apiConfig.tooltip.pointFormat) {
            const yToken = this.getApiTooltipYValueToken(apiConfig);
            apiConfig.tooltip.pointFormat = apiConfig.tooltip.pointFormat.replace(/{point\.y(?![.:])}/g, yToken);
            apiConfig.tooltip.pointFormat = apiConfig.tooltip.pointFormat.replace(/{point\.y:[^}]*}/g, yToken);

            // Pie/donut: values are already shares/percentages — always show 2 decimal places
            apiConfig.tooltip.pointFormat = apiConfig.tooltip.pointFormat.replace(/{point\.percentage(?![.:])}/g, '{point.y:.2f}%');
            apiConfig.tooltip.pointFormat = apiConfig.tooltip.pointFormat.replace(/{point\.percentage:[^}]*}/g, '{point.y:.2f}%');
          }
        }

        this.chartOptions = {
          ...apiConfig,
          title: {
            text: ''
          },
          subtitle: {
            text: subtitleText
          },
          credits: {
            enabled: false
          },
          exporting: this.getExportingConfig(),
          lang: {
            ...(apiConfig.lang || {}),
            thousandsSep: ''
          }
        };
        return;
      }

      const normalizedData = this.normalizeData(this.chartData.data, chartType);

      // Check if data is empty after normalization
      if (this.isDataEmpty(normalizedData)) {
        this.hasError = true;
        this.errorMessage = 'No data found';
        this.chartOptions = {};
        return;
      }

      switch (chartType) {
        case 'bar':
          this.chartOptions = this.createBarChartOptions(normalizedData);
          break;
        case 'column':
          this.chartOptions = this.createColumnChartOptions(normalizedData);
          break;
        case 'line':
        case 'spline':
          this.chartOptions = this.createLineChartOptions(normalizedData, chartType);
          break;
        case 'pie':
          this.chartOptions = this.createPieChartOptions(normalizedData);
          break;
        case 'donut':
          this.chartOptions = this.createDonutChartOptions(normalizedData);
          break;
        case 'area':
        case 'areaspline':
          this.chartOptions = this.createAreaChartOptions(normalizedData, chartType);
          break;
        case 'scatter':
          this.chartOptions = this.createScatterChartOptions(normalizedData);
          break;
        case 'gauge':
          this.chartOptions = this.createGaugeChartOptions(normalizedData);
          break;
        case 'heatmap':
          this.chartOptions = this.createHeatmapChartOptions(normalizedData);
          break;
        case 'treemap':
          this.chartOptions = this.createTreemapChartOptions(normalizedData);
          break;
        case 'sankey':
          this.chartOptions = this.createSankeyChartOptions(normalizedData);
          break;
        case 'bubble':
          this.chartOptions = this.createBubbleChartOptions(normalizedData);
          break;
        case 'waterfall':
          this.chartOptions = this.createWaterfallChartOptions(normalizedData);
          break;
        case 'polar':
          this.chartOptions = this.createPolarChartOptions(normalizedData);
          break;
        default:
          // console.warn(`Unknown chart type: ${chartType}, defaulting to column chart`);
          this.chartOptions = this.createColumnChartOptions(normalizedData);
      }

      // Title is displayed in the HTML header, so we keep Highcharts title empty
      // to avoid showing it twice
    } catch (error: any) {
      console.error('Error updating chart:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Failed to render chart';
    }
  }

  isDataEmpty(normalizedData: any): boolean {
    // Check if series exists and has data
    if (!normalizedData.series || normalizedData.series.length === 0) {
      return true;
    }

    // Check if any series has actual data points
    const hasDataPoints = normalizedData.series.some((series: any) => {
      if (!series.data || !Array.isArray(series.data)) {
        return false;
      }
      return series.data.length > 0;
    });

    return !hasDataPoints;
  }

  normalizeData(data: any, chartType?: string): any {
    // Handle different API response formats
    let normalized: any = {
      categories: [],
      series: [],
      data: [],
      xAxisLabel: '',
      yAxisLabel: ''
    };

    // If data is already normalized, return as is
    if (data.categories && data.series) {
      return data;
    }

    if (Array.isArray(data.categories)) {
      normalized.categories = normalizeCategoryArray(data.categories);
    } else if (data.x_axis && Array.isArray(data.x_axis)) {
      normalized.categories = normalizeCategoryArray(data.x_axis);
    } else if (data.labels && Array.isArray(data.labels)) {
      normalized.categories = normalizeCategoryArray(data.labels);
    }

    // Check if this is a time series chart (line, spline, area, areaspline with timestamp data)
    const isTimeSeriesChart = (seriesData: any[]): boolean => {
      if (!Array.isArray(seriesData) || seriesData.length === 0) {
        return false;
      }
      const firstItem = seriesData[0];
      // Check if first item is [timestamp, value] format
      if (Array.isArray(firstItem) && firstItem.length >= 2) {
        const timestamp = firstItem[0];
        // If timestamp is a string that looks like a date, it's time series
        if (typeof timestamp === 'string' && (timestamp.includes('-') || timestamp.includes('T'))) {
          return true;
        }
      }
      return false;
    };

    // Extract series - handle different data formats based on chart type
    const cleanSeriesData = (seriesData: any[], isTimeSeries: boolean = false): any[] => {
      if (!Array.isArray(seriesData)) {
        return [];
      }

      return seriesData.filter((item: any) => {
        // For time series: [["timestamp", value], ...]
        if (isTimeSeries && Array.isArray(item) && item.length >= 2) {
          return item[1] !== null && item[1] !== undefined;
        }
        // For pie/donut: [{"name": "...", "y": value}, ...] or [["name", value], ...]
        if (Array.isArray(item) && item.length >= 2) {
          return item[1] !== null && item[1] !== undefined;
        }
        // For objects with y or value properties
        if (typeof item === 'object' && item !== null) {
          return (item.y !== null && item.y !== undefined) || (item.value !== null && item.value !== undefined);
        }
        // For simple values
        return item !== null && item !== undefined;
      }).map((item: any) => {
        // Handle time series format: [["timestamp", value], ...]
        if (isTimeSeries && Array.isArray(item) && item.length >= 2) {
          const timestamp = item[0];
          let value = item[1];
          // Convert timestamp string to number (milliseconds since epoch)
          if (typeof timestamp === 'string') {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
              return [date.getTime(), typeof value === 'string' ? parseFloat(value) || 0 : (value || 0)];
            }
          }
          // If timestamp is already a number, use it directly
          return [typeof timestamp === 'string' ? parseFloat(timestamp) || 0 : timestamp,
                  typeof value === 'string' ? parseFloat(value) || 0 : (value || 0)];
        }
        // Handle pie/donut format: [["name", value], ...]
        if (Array.isArray(item) && item.length >= 2) {
          const name = normalizeChartDisplayLabel(item[0]);
          const value = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
          return [name, value];
        }
        // Handle pie/donut format: [{"name": "...", "y": value}, ...]
        if (typeof item === 'object' && item !== null) {
          const name = normalizeChartDisplayLabel(item.name ?? item.label ?? '');
          const value = item.y !== undefined ? item.y : item.value;
          const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
          return { name, y: numValue };
        }
        // Convert simple string numbers to actual numbers
        if (typeof item === 'string' && !isNaN(parseFloat(item)) && isFinite(parseFloat(item))) {
          return parseFloat(item);
        }
        // Keep numbers as-is
        if (typeof item === 'number') {
          return item;
        }
        return item;
      });
    };

    if (Array.isArray(data.series)) {
      normalized.series = data.series.map((s: any) => {
        if (typeof s === 'object' && s !== null) {
          const seriesData = Array.isArray(s.data) ? s.data : (s.values || []);
          const isTimeSeries = isTimeSeriesChart(seriesData);
          return {
            name: normalizeChartDisplayLabel(s.name ?? s.label ?? ''),
            type: s.type || chartType || 'line',
            data: cleanSeriesData(seriesData, isTimeSeries),
            color: s.color,
            yAxis: s.yAxis
          };
        }
        return s;
      });
    } else if (data.data && Array.isArray(data.data)) {
      // Single series format
      const isTimeSeries = isTimeSeriesChart(data.data);
      normalized.series = [{
        name: 'Data',
        type: chartType || 'line',
        data: cleanSeriesData(data.data, isTimeSeries)
      }];
    } else if (data.values && Array.isArray(data.values)) {
      const isTimeSeries = isTimeSeriesChart(data.values);
      normalized.series = [{
        name: 'Data',
        type: chartType || 'line',
        data: cleanSeriesData(data.values, isTimeSeries)
      }];
    } else if (typeof data === 'object' && data !== null) {
      // Try to extract series from object keys
      const seriesKeys = Object.keys(data).filter(key =>
        !['categories', 'x_axis', 'labels', 'xAxisLabel', 'yAxisLabel', 'chart_id', 'id', 'table_data'].includes(key)
      );

      if (seriesKeys.length > 0) {
        normalized.series = seriesKeys.map(key => {
          const seriesData = Array.isArray(data[key]) ? data[key] : [];
          const isTimeSeries = isTimeSeriesChart(seriesData);
          return {
            name: normalizeChartDisplayLabel(key),
            type: 'line',
            data: cleanSeriesData(seriesData, isTimeSeries)
          };
        });
      }
    }

    // Extract axis labels
    normalized.xAxisLabel = data.xAxisLabel || data.x_axis_label || data.x_label || '';
    normalized.yAxisLabel = data.yAxisLabel || data.y_axis_label || data.y_label || 'Value';

    // For pie/donut charts, extract data array
    if (data.data && Array.isArray(data.data)) {
      normalized.data = data.data;
    } else if (data.values && Array.isArray(data.values)) {
      normalized.data = data.values;
    } else if (normalized.series && normalized.series.length > 0 && normalized.series[0].data) {
      normalized.data = normalized.series[0].data;
    }

    return normalized;
  }

  createBarChartOptions(data: any): Highcharts.Options {
    // Format data for table display with name, count, percentage structure
    const formattedData = this.formatBarColumnData(data.series?.[0]?.data || data.data || []);

    // Update table_data if it exists
    if (this.chartData && this.chartData.data) {
      this.chartData.data.table_data = formattedData;
    }

    return {
      chart: {
        type: 'bar',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: data.series && data.series.length > 1,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'top'
      },
      tooltip: {
        shared: false
      }
    };
  }

  createColumnChartOptions(data: any): Highcharts.Options {
    // Format data for table display with name, count, percentage structure
    const formattedData = this.formatBarColumnData(data.series?.[0]?.data || data.data || []);

    // Update table_data if it exists
    if (this.chartData && this.chartData.data) {
      this.chartData.data.table_data = formattedData;
    }

    return {
      chart: {
        type: 'column',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: data.series && data.series.length > 1,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'top'
      },
      tooltip: {
        shared: false
      }
    };
  }

  createLineChartOptions(data: any, chartType: string = 'line'): Highcharts.Options {
    // Check if series data is time series format (nested arrays with timestamps)
    const isTimeSeries = data.series && data.series.length > 0 &&
                        data.series[0].data &&
                        Array.isArray(data.series[0].data) &&
                        data.series[0].data.length > 0 &&
                        Array.isArray(data.series[0].data[0]) &&
                        typeof data.series[0].data[0][0] === 'number';

    return {
      chart: {
        type: chartType as any,
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: isTimeSeries ? {
        type: 'datetime',
        title: {
          text: data.xAxisLabel || 'Date'
        }
      } : {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true
      },
      tooltip: {
        shared: true
      }
    };
  }

  createPieChartOptions(data: any): Highcharts.Options {
    const pieData = data.series?.[0]?.data || data.data || [];
    return {
      chart: {
        type: 'pie',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      series: [{
        type: 'pie',
        name: data.series?.[0]?.name || 'Data',
        data: this.formatPieData(pieData)
      }],
      legend: {
        enabled: true,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.2f}%</b>'
      }
    };
  }

  createDonutChartOptions(data: any): Highcharts.Options {
    const pieData = data.series?.[0]?.data || data.data || [];
    return {
      chart: {
        type: 'pie',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      plotOptions: {
        pie: {
          innerSize: '50%'
        }
      },
      series: [{
        type: 'pie',
        name: data.series?.[0]?.name || 'Data',
        data: this.formatPieData(pieData)
      }],
      legend: {
        enabled: true,
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle'
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.2f}%</b>'
      }
    };
  }

  createAreaChartOptions(data: any, chartType: string = 'area'): Highcharts.Options {
    // Check if series data is time series format (nested arrays with timestamps)
    const isTimeSeries = data.series && data.series.length > 0 &&
                        data.series[0].data &&
                        Array.isArray(data.series[0].data) &&
                        data.series[0].data.length > 0 &&
                        Array.isArray(data.series[0].data[0]) &&
                        typeof data.series[0].data[0][0] === 'number';

    return {
      chart: {
        type: chartType as any,
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: isTimeSeries ? {
        type: 'datetime',
        title: {
          text: data.xAxisLabel || 'Date'
        }
      } : {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: this.ensureSeriesFormat(data.series || [], chartType),
      legend: {
        enabled: true
      },
      plotOptions: {
        area: {
          stacking: 'normal'
        }
      },
      tooltip: {
        shared: true
      }
    };
  }

  createScatterChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'scatter',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        title: {
          text: data.xAxisLabel || 'X Axis'
        },
        type: 'linear'
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Y Axis'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true
      },
      tooltip: {
        pointFormat: 'X: {point.x}<br/>Y: {point.y}'
      }
    };
  }

  createGaugeChartOptions(data: any): Highcharts.Options {
    const value = this.extractGaugeValue(data);
    // Try to get min/max from yAxis config if available
    const min = data.yAxis?.min !== undefined ? data.yAxis.min : 0;
    const max = data.yAxis?.max !== undefined ? data.yAxis.max : 100;

    return {
      chart: {
        type: 'gauge',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      pane: {
        startAngle: -90,
        endAngle: 90
      },
      yAxis: {
        min: min,
        max: max,
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: [{
        type: 'gauge',
        name: data.series?.[0]?.name || 'Value',
        data: [value],
        tooltip: {
          valueSuffix: ''
        }
      }],
      plotOptions: {
        gauge: {
          dial: {
            radius: '100%',
            backgroundColor: 'gray',
            baseWidth: 12,
            baseLength: '0%',
            rearLength: '0%'
          },
          pivot: {
            backgroundColor: 'gray'
          }
        }
      }
    };
  }

  createHeatmapChartOptions(data: any): Highcharts.Options {
    const heatmapData = this.formatHeatmapData(data);

    // Extract unique dimensions for x and y axes
    const xCategories = new Set<string>();
    const yCategories = new Set<string>();

    heatmapData.forEach((item: any) => {
      if (Array.isArray(item) && item.length >= 3) {
        xCategories.add(String(item[0]));
        yCategories.add(String(item[1]));
      }
    });

    return {
      chart: {
        type: 'heatmap',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      colorAxis: {
        min: 0,
        minColor: '#FFFFFF',
        maxColor: '#0000FF'
      },
      xAxis: {
        categories: Array.from(xCategories),
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        categories: Array.from(yCategories),
        title: {
          text: data.yAxisLabel || ''
        }
      },
      series: [{
        type: 'heatmap',
        name: data.series?.[0]?.name || 'Data',
        data: heatmapData
      }],
      tooltip: {
        formatter: function() {
          const point = this.point as any;
          const xCat = point.series.xAxis.categories[point.x];
          const yCat = point.series.yAxis.categories[point.y];
          return `<b>${xCat}</b><br><b>${yCat}</b><br>Value: <b>${point.value}</b>`;
        }
      }
    };
  }

  createTreemapChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'treemap',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      series: [{
        type: 'treemap',
        name: data.series?.[0]?.name || 'Data',
        data: this.formatTreemapData(data)
      }],
      tooltip: {
        pointFormat: '<b>{point.name}</b>: {point.value}'
      }
    };
  }

  createSankeyChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'sankey',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      colors: SENTIMENT_SANKEY_COLORS,
      plotOptions: {
        sankey: {
          colors: SENTIMENT_SANKEY_COLORS
        }
      },
      series: [{
        type: 'sankey',
        name: 'Flow',
        keys: ['from', 'to', 'weight'],
        data: this.formatSankeyData(data),
        nodes: data.nodes || [],
        colorByPoint: true,
        colors: SENTIMENT_SANKEY_COLORS
      }],
      tooltip: {
        useHTML: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const point = this.point as any;
          const fromName = point.from || point.fromNode?.name || '';
          const toName = point.to || point.toNode?.name || '';
          const pctRaw = point.percent ?? point.options?.percent;
          const pct =
            typeof pctRaw === 'number' && !isNaN(pctRaw)
              ? pctRaw
              : pctRaw != null && !isNaN(Number(pctRaw))
                ? Number(pctRaw)
                : undefined;
          const w = point.weight ?? point.y;
          if (pct !== undefined) {
            const pctStr =
              pct.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                useGrouping: false,
              }) + '%';
            return (
              `<div style="font-family:Lexend,sans-serif">` +
              `<div style="font-size:11px;color:#6b7280;font-weight:600;">${fromName} → ${toName}</div>` +
              `<div style="font-size:15px;font-weight:700;margin-top:4px">${pctStr}</div>` +
              `</div>`
            );
          }
          const val =
            typeof w === 'number'
              ? w.toLocaleString(undefined, { useGrouping: false })
              : String(w ?? '');
          return `<b>${fromName} → ${toName}</b><br/>${val}`;
        },
      },
    };
  }

  createBubbleChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'bubble',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        title: {
          text: data.xAxisLabel || 'X Axis'
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Y Axis'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true
      },
      tooltip: {
        pointFormat: 'X: {point.x}<br/>Y: {point.y}<br/>Size: {point.z}'
      }
    };
  }

  createWaterfallChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'waterfall',
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: false
      }
    };
  }

  createPolarChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'line',
        polar: true,
        height: 350,
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        tickmarkPlacement: 'on',
        lineWidth: 0
      },
      yAxis: {
        gridLineInterpolation: 'polygon',
        lineWidth: 0,
        min: 0
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true
      }
    };
  }

  /**
   * Highcharts tooltip token for series Y: whole numbers for volume/count-style metrics,
   * two decimals when the chart is percentage-based or axis/metric indicates percentages.
   */
  private getApiTooltipYValueToken(apiConfig: any): string {
    const metricType = String(
      this.chartData?.data?.metric_type ??
      (this.chartData as any)?.metric_type ??
      ''
    ).toLowerCase();

    if (metricType === 'percentage') {
      return '{point.y:.2f}';
    }
    if (metricType === 'volume') {
      return '{point.y:.0f}';
    }

    const yAxis = apiConfig?.yAxis;
    const yTitle = Array.isArray(yAxis)
      ? String(yAxis[0]?.title?.text ?? '')
      : String(yAxis?.title?.text ?? '');
    const yLower = yTitle.toLowerCase();
    if (yLower.includes('volume') || yLower.includes('count')) {
      return '{point.y:.0f}';
    }

    return '{point.y:.2f}';
  }

  // Helper methods
  getExportingConfig(): Highcharts.ExportingOptions {
    return {
      enabled: false,
      menuItemDefinitions: {
        // viewFullscreen: {
        //   text: 'View in Full Screen'
        // },
        // printChart: {
        //   text: 'Print Chart'
        // },
        // downloadPNG: {
        //   text: 'Download as PNG'
        // },
        downloadJPEG: {
          text: 'Download Image'
        },
        // downloadPDF: {
        //   text: 'Download PDF'
        // },
        // downloadSVG: {
        //   text: 'Download as SVG'
        // },
        // downloadCSV: {
        //   text: 'Download Chart Table'
        // },
        // downloadXLS: {
        //   text: 'Download Excel File'
        // },
        // viewData: {
        //   text: 'View Data Table'
        // }
      },
      buttons: {
        contextButton: {
          enabled: false,
          menuItems: [
            // 'viewFullscreen',
            // 'printChart',
            // 'separator',
            // 'downloadPNG',
            'downloadJPEG',
            // 'downloadPDF',
            // 'downloadSVG',
            // 'separator',
            // 'downloadCSV',
            // 'downloadXLS',
            // 'viewData'
          ],
          x: -10,
          y: 5,
          symbol: 'menu',
          symbolStrokeWidth: 2
        }
      }
    };
  }

  ensureSeriesFormat(series: any[], defaultSeriesType?: string): any[] {
    if (!Array.isArray(series)) {
      return [];
    }
    const typeDefault = defaultSeriesType || 'line';
    return series.map(s => {
      if (typeof s === 'object' && s !== null) {
        return {
          ...s,
          name: normalizeChartDisplayLabel(s.name ?? ''),
          type: (typeof s.type === 'string' && s.type.length > 0) ? s.type : typeDefault,
          data: Array.isArray(s.data) ? s.data : (s.values || [])
        };
      }
      return s;
    });
  }

  formatPieData(data: any[]): any[] {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map(item => {
      // Handle pie/donut format: [{"name": "...", "y": value}, ...]
      if (typeof item === 'object' && item !== null) {
        const name = normalizeChartDisplayLabel(item.name ?? item.label ?? '');
        const value = item.y !== undefined ? item.y : item.value;
        const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
        return { name, y: numValue };
      }
      // Handle pie/donut format: [["name", value], ...]
      if (Array.isArray(item) && item.length >= 2) {
        const name = normalizeChartDisplayLabel(item[0]);
        const numValue = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
        return { name, y: numValue };
      }
      // Fallback for simple values
      const numValue = typeof item === 'string' ? parseFloat(item) || 0 : (item || 0);
      return { name: normalizeChartDisplayLabel(''), y: numValue };
    });
  }

  formatBarColumnData(data: any[]): any[] {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map(item => {
      // Handle format: [{"name": "...", "y": value}, ...] or [{"name": "...", "count": value, "percentage": value}, ...]
      if (typeof item === 'object' && item !== null) {
        const name = normalizeChartDisplayLabel(item.name ?? item.label ?? '');
        let count, percentage;

        if (item.count !== undefined && item.percentage !== undefined) {
          // Already in the correct format: name, count, percentage
          count = typeof item.count === 'string' ? parseFloat(item.count) || 0 : (item.count || 0);
          percentage = typeof item.percentage === 'string' ? parseFloat(item.percentage) || 0 : (item.percentage || 0);
        } else {
          // Convert from y/value to count and calculate percentage
          const value = item.y !== undefined ? item.y : item.value;
          count = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
          percentage = count; // Will be calculated later if needed
        }

        return { name, count, percentage };
      }
      // Handle array format: [["name", value], ...]
      if (Array.isArray(item) && item.length >= 2) {
        const name = normalizeChartDisplayLabel(item[0]);
        const count = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
        return { name, count, percentage: count };
      }
      // Fallback for simple values
      const count = typeof item === 'string' ? parseFloat(item) || 0 : (item || 0);
      return { name: normalizeChartDisplayLabel(''), count, percentage: count };
    });
  }

  formatHeatmapData(data: any): any[] {
    // Heatmap data format: [["dim1", "dim2", value], ...]
    const heatmapData = data.series?.[0]?.data || data.data || [];
    if (!Array.isArray(heatmapData)) {
      return [];
    }
    // Heatmap data should already be in the correct format from API
    // Just ensure values are numbers
    return heatmapData.map((item: any) => {
      if (Array.isArray(item) && item.length >= 3) {
        const dim1 = normalizeChartDisplayLabel(item[0]);
        const dim2 = normalizeChartDisplayLabel(item[1]);
        const value = typeof item[2] === 'string' ? parseFloat(item[2]) || 0 : (item[2] || 0);
        return [dim1, dim2, value];
      }
      return item;
    });
  }

  formatTreemapData(data: any): any[] {
    // Treemap data format: [{name: 'A', value: 1}, ...]
    const treemapData = data.series?.[0]?.data || data.data || [];
    if (!Array.isArray(treemapData)) {
      return [];
    }
    return treemapData.map((item: any) => {
      // Handle treemap format: [{"name": "...", "value": value}, ...]
      if (typeof item === 'object' && item !== null) {
        const name = normalizeChartDisplayLabel(item.name ?? '');
        const value = item.value !== undefined ? item.value : item.y;
        const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
        return { name, value: numValue };
      }
      // Handle array format: [["name", value], ...]
      if (Array.isArray(item) && item.length >= 2) {
        const name = normalizeChartDisplayLabel(item[0]);
        const numValue = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
        return { name, value: numValue };
      }
      return { name: normalizeChartDisplayLabel(''), value: 0 };
    });
  }

  formatSankeyData(data: any): any[] {
    // Sankey data format: [['A', 'B', 10], ...] or { from, to, weight, percent? }[]
    if (Array.isArray(data.links)) {
      return data.links.map((link: any) => {
        const from = normalizeChartDisplayLabel(link.source ?? link.from ?? '');
        const to = normalizeChartDisplayLabel(link.target ?? link.to ?? '');
        const weight = Number(link.value ?? link.weight ?? 0) || 0;
        const percentRaw = link.percent ?? link.Percent ?? link.percentage ?? link.Percentage;
        if (percentRaw !== undefined && percentRaw !== null && !isNaN(Number(percentRaw))) {
          return { from, to, weight, percent: Number(percentRaw) };
        }
        return [from, to, link.value ?? link.weight ?? 0];
      });
    }
    if (Array.isArray(data.data)) {
      return data.data.map((row: any) => {
        if (Array.isArray(row) && row.length >= 3) {
          return [
            normalizeChartDisplayLabel(row[0]),
            normalizeChartDisplayLabel(row[1]),
            ...row.slice(2)
          ];
        }
        return row;
      });
    }
    return [];
  }

  extractGaugeValue(data: any): number {
    // Gauge format: [value] - single value array
    if (data.series && data.series[0] && data.series[0].data) {
      const seriesData = data.series[0].data;
      if (Array.isArray(seriesData) && seriesData.length > 0) {
        const value = seriesData[0];
        return typeof value === 'string' ? parseFloat(value) || 0 : (Number(value) || 0);
      }
      return Number(seriesData) || 0;
    }
    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      const value = data.data[0];
      return typeof value === 'string' ? parseFloat(value) || 0 : (Number(value) || 0);
    }
    if (data.value !== undefined) {
      return typeof data.value === 'string' ? parseFloat(data.value) || 0 : (Number(data.value) || 0);
    }
    return 0;
  }

  onExpand() {
    this.showMenu = false;

    if (!this.chartData) {
      console.error('No chart data available for large view');
      return;
    }

    // Check for unsaved changes when in edit mode
    if (this.isEditMode && this.hasUnsavedChanges) {
      // Show confirmation dialog for unsaved changes
      firstValueFrom(this.confirmPopupService.openConfirmDialog({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes to your dashboard. Please save the latest changes of dashboard to view chart in large mode.',
        confirmText: 'Save & View',
        cancelText: 'Cancel',
        confirmButtonClass: 'save-button'
      })).then((confirmed: boolean) => {
        if (confirmed) {
          // User wants to save first - emit save request to parent
          this.saveAndLargeViewRequested.emit();
        }
        // If not confirmed, do nothing (stay on current page)
      });
      return;
    }

    this.navigateToLargeView();
  }

  navigateToLargeView() {
    // Store chart data in service
    this.largeChartViewService.setChartData(this.chartData);

    // Store the current dashboard ID in localStorage for back navigation
    // Get the dashboard ID from the parent component (create-dashboard)
    const currentUrl = this.router.url;
    const urlParams = new URLSearchParams(currentUrl.split('?')[1] || '');
    const dashboardId = urlParams.get('id');

    if (dashboardId) {
      sessionStorage.setItem('lastViewedDashboardId', dashboardId);
    }

    // Navigate to chart view page
    this.router.navigate(['chart-view']);
  }

  onMore(event: Event) {
    event.stopPropagation();
    this.showMenu = !this.showMenu;
  }

  onEdit() {
    this.showMenu = false;
    this.editRequested.emit();
  }

  onDelete() {
    this.showMenu = false;
    this.deleteRequested.emit();
  }


  onDownload() {
    this.showMenu = false;
    this.downloadRequested.emit();

    if (!this.chartData) {
      this.notificationService.error('Error', 'Chart data not available. Cannot export to CSV.');
      return;
    }

    if (downloadChartTableCsv(this.chartData)) {
      this.notificationService.success('Download Complete', 'CSV file download started');
      return;
    }

    this.notificationService.error(
      'Nothing to export',
      'This chart has no table data loaded. Try another chart or adjust filters.'
    );
  }

  onDownloadImage() {
    this.showMenu = false;

    if (!this.chartRef) {
      console.error('Chart reference not available for image export');
      this.notificationService.error('Error', 'Chart not ready for image export');
      return;
    }

    try {
      // Show processing notification
      const processingNotification = this.notificationService.processing(
        'Generating Image',
        'Generating chart image...'
      );

      const svgExportOpts = buildHighchartsGetSvgExportOptions(this.chartOptions as Highcharts.Options);
      const svg = this.chartRef.getSVG(svgExportOpts);

      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        processingNotification.dismiss();
        this.notificationService.error('Export Failed', 'Canvas context not available');
        return;
      }

      const img = new Image();
      const headerPx = 80;

      img.onload = () => {
        URL.revokeObjectURL(svgUrl);
        try {
          const { canvasWidth, canvasHeight, drawWidth, drawHeight } = fitChartExportCanvas(
            img.naturalWidth,
            img.naturalHeight,
            headerPx
          );
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;

          // Draw white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw header information
          ctx.fillStyle = '#333333';
          ctx.font = 'bold 24px Arial';
          ctx.fillText(this.chartData?.title || 'Chart', 50, 40);

          // Draw last updated timestamp
          ctx.font = '14px Arial';
          const lastUpdated = this.getLastUpdatedTime();
          if (lastUpdated) {
            ctx.fillStyle = '#666666';
            ctx.fillText(`Last Updated: ${lastUpdated}`, 50, 65);
          }

          // Preserve SVG aspect ratio — avoids squashing many categories into 1200px
          ctx.drawImage(img, 0, headerPx, drawWidth, drawHeight);

          // Convert to blob and download
          canvas.toBlob((blob: Blob | null) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${this.getChartFileName()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              processingNotification.dismiss();
              this.notificationService.success('Download Complete', 'Chart exported.');
            } else {
              processingNotification.dismiss();
              this.notificationService.error('Export Failed', 'Failed to create image blob');
            }
          }, 'image/png');
        } catch (error) {
          console.error('Error processing image:', error);
          processingNotification.dismiss();
          this.notificationService.error('Export Failed', 'Failed to process chart image');
        }
      };

      img.onerror = () => {
        console.error('Error loading SVG as image');
        processingNotification.dismiss();
        this.notificationService.error('Export Failed', 'Failed to generate chart image');
      };

      const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      img.src = svgUrl;

    } catch (error) {
      console.error('Error downloading chart image:', error);
      this.notificationService.error('Export Failed', 'Failed to download chart image');
    }
  }

  getChartFileName(): string {
    // Generate filename from chart title and current timestamp
    const sanitizedTitle = (this.chartData?.title || 'chart')
      .replace(/[^a-z0-9]/gi, '_')
      .toLowerCase();

    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, -5); // Remove milliseconds and Z

    return `${sanitizedTitle}_${timestamp}`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.menu-container')) {
      this.showMenu = false;
    }
  }

  getSubtitle(): string | null {
    // Get subtitle from config.subtitle.text
    if (this.chartData?.config?.subtitle?.text) {
      return this.chartData.config.subtitle.text;
    }
    return null;
  }

  getDuration(): string | null {
    // Return subtitle from API as-is
    return this.getSubtitle();
  }

  /** Timezone selected for this chart (e.g. EST). Used for subtitle and Last updated. */
  getChartTimezone(): string {
    const c = this.chartData as any;
    const tz = c?.time_zone ?? this.chartData?.config?.time_zone ?? c?.criteria?.time_zone ?? c?.TimeZone;
    return (tz && String(tz).trim()) ? String(tz).trim() : 'EST';
  }

  formatUTCDateTime(date: Date): string {
    // Format in the chart's selected timezone, e.g. "Jan 19, 2026 11:36:00 EST"
    return formatDateTimeInTimezone(date, this.getChartTimezone());
  }

  formatUserFriendlyDateTime(date: Date): string {
    // Format in the chart's selected timezone, e.g. "10 Feb 2025 3:21:28 PM EST"
    return formatUserFriendlyDateTimeInTimezone(date, this.getChartTimezone());
  }

  formatSubtitleText(subtitleText: string): string {
    // Parse subtitle format: "From 2022-12-31T18:30:00Z to 2026-01-08T18:29:59.999000Z"
    // or "2022-12-31T18:30:00Z to 2026-01-08T18:29:59.999000Z"
    const dateRangeMatch = subtitleText.match(/(\d{4}-\d{2}-\d{2}T[\d:\.Z]+)\s+to\s+(\d{4}-\d{2}-\d{2}T[\d:\.Z]+)/i);
    if (dateRangeMatch) {
      try {
        const startDate = new Date(dateRangeMatch[1].trim());
        const endDate = new Date(dateRangeMatch[2].trim());

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const startFormatted = this.formatUserFriendlyDateTime(startDate);
          const endFormatted = this.formatUserFriendlyDateTime(endDate);
          // Check if "From" is already in the original text
          const hasFromPrefix = /^from\s+/i.test(subtitleText);
          return hasFromPrefix
            ? `From ${startFormatted} to ${endFormatted}`
            : `${startFormatted} to ${endFormatted}`;
        }
      } catch (error) {
        // console.warn('Error parsing subtitle date range:', error);
      }
    }
    // If parsing fails, return original text
    return subtitleText;
  }

  getLastUpdatedTime(): string {
    // Prefer last_updated_timestamp (data refresh / LastGeneratedAt) for both regular and fixed ranges
    const timestamp = this.chartData?.last_updated_timestamp
      || this.chartData?.updated_at
      || this.chartData?.generated_at;

    if (timestamp) {
      try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return this.formatUTCDateTime(date);
        }
      } catch (error) {
        // console.warn('Error parsing timestamp:', error);
      }
    }

    // Fallback if timestamp is not available or invalid
    return '';
  }

  getLastEditedTime(): string {
    const timestamp = this.chartData?.last_edited_at
      || this.chartData?.updated_at
      || this.chartData?.generated_at;

    if (!timestamp) {
      return '';
    }

    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return this.formatUTCDateTime(date);
      }
    } catch (error) {
      // console.warn('Error parsing last edited timestamp:', error);
    }

    return '';
  }

  getLastEditedBy(): string {
    const chartDataAny = this.chartData as any;
    const editor = this.chartData?.updated_by
      || chartDataAny?.updated_by_email
      || this.chartData?.generated_by
      || chartDataAny?.generated_by_email
      || chartDataAny?.CreatedByEmail
      || chartDataAny?.UpdatedByEmail;

    return formatDashboardUserEmail(editor);
  }

  getDurationBehavior(): string | null {
    // Get DurationBehavior from chart data (from API response)
    if (!this.chartData) {
      return null;
    }

    // Check multiple possible locations for DurationBehavior/chart_duration_tag
    const chartDataAny = this.chartData as any;
    const durationBehavior = this.chartData.DurationBehavior
      || chartDataAny.chart_duration_tag
      || chartDataAny.duration_behavior
      || chartDataAny.durationBehavior
      || this.chartData.config?.chart_duration_tag
      || this.chartData.config?.DurationBehavior
      || (this.chartData.config as any)?.duration_behavior
      || (this.chartData.config as any)?.durationBehavior;

    return durationBehavior || null;
  }

  getDurationBehaviorLabel(): string | null {
    const behavior = this.getDurationBehavior();
    if (!behavior) {
      return null;
    }

    // Map DurationBehavior values to short, concise labels
    // Handle both API format (dynamic_this_month) and dropdown format (this_month)
    switch (behavior.toLowerCase()) {
      case 'fixed':
        return 'Fixed';
      case 'dynamic_today':
      case 'today':
        return 'Today';
      case 'dynamic_this_week':
      case 'this_week':
        return 'This Week';
      case 'dynamic_this_month':
      case 'this_month':
        return 'This Month';
      case 'dynamic_this_quarter':
      case 'this_quarter':
        return 'This Quarter';
      case 'dynamic_this_year':
      case 'this_year':
        return 'This Year';
      case 'dynamic_last_n_days':
      case 'last_n_days':
        // Extract the actual N value from chart data
        const lastNDays = this.getLastNDaysValue();
        return lastNDays ? `Last ${lastNDays} Days` : 'Last N Days';
      default:
        // For any other values, try to make them user-friendly
        return behavior.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  }

  private getLastNDaysValue(): number | null {
    if (!this.chartData) {
      return null;
    }

    const chartDataAny = this.chartData as any;

    // Check multiple possible locations for last_n_days value
    // Prioritize chartData.Description.criteria_applied.last_n_days based on the provided JSON structure
    return chartDataAny.Description?.criteria_applied?.last_n_days ||
           chartDataAny.criteria?.last_n_days ||
           chartDataAny.config?.criteria?.last_n_days ||
           chartDataAny.last_n_days ||
           chartDataAny.lastNDays ||
           chartDataAny.config?.last_n_days ||
           (chartDataAny.config as any)?.duration_behavior?.last_n_days ||
           chartDataAny.data?.criteria?.last_n_days ||
           chartDataAny.data?.last_n_days ||
           null;
  }

  getDurationBehaviorClass(): string | null {
    const behavior = this.getDurationBehavior();
    if (!behavior) {
      return null;
    }

    // Return CSS class name for styling
    // Handle both API format (dynamic_this_month) and dropdown format (this_month)
    switch (behavior.toLowerCase()) {
      case 'fixed':
        return 'duration-badge-fixed';
      case 'dynamic_today':
      case 'today':
        return 'duration-badge-today';
      case 'dynamic_this_week':
      case 'this_week':
        return 'duration-badge-this-week';
      case 'dynamic_this_month':
      case 'this_month':
        return 'duration-badge-this-month';
      case 'dynamic_this_quarter':
      case 'this_quarter':
        return 'duration-badge-this-quarter';
      case 'dynamic_this_year':
      case 'this_year':
        return 'duration-badge-this-year';
      case 'dynamic_last_n_days':
      case 'last_n_days':
        return 'duration-badge-last-n-days';
      default:
        return 'duration-badge-default';
    }
  }

  getAppliedFilters(): string {
    // Prefer top-level filters; fallback to config.filters (e.g. when loaded from dashboard API)
    const filters = this.chartData?.filters && Array.isArray(this.chartData.filters) && this.chartData.filters.length > 0
      ? this.chartData.filters
      : (this.chartData?.config?.filters && Array.isArray(this.chartData.config.filters) ? this.chartData.config.filters : []);
    if (filters.length === 0) {
      return '';
    }

    return filters.map((filter: any) => {
      const field = this.mapFieldName(filter.columnName ?? '');
      const operator = filter.operationType ?? '';
      const value = Array.isArray(filter.value) ? filter.value.join(', ') : String(filter.value ?? '');
      return `${field} ${operator} ${value}`;
    }).join(' | ');
  }

  mapFieldName(fieldName: string): string {
    const fieldMappings: { [key: string]: string } = {
      // Queue related fields
      'QueueName': 'Queue Name',
      'queue_name': 'Queue Name',
      'Queue': 'Queue',

      // Call related fields
      'CallType': 'Call Type',
      'call_type': 'Call Type',
      'CallDirection': 'Call Direction',
      'call_direction': 'Call Direction',

      // Agent related fields
      'AgentUsername': 'Agent Username',
      'agent_username': 'Agent Username',
      'AgentName': 'Agent Name',
      'agent_name': 'Agent Name',

      // Customer related fields
      'CustomerSentimentAtStart': 'Customer Sentiment (Start)',
      'customersentimentatstart': 'Customer Sentiment (Start)',
      'CustomerSentimentAtEnd': 'Customer Sentiment (End)',
      'customersentimentatend': 'Customer Sentiment (End)',

      // Topic and intent
      'PrimaryTopic': 'Primary Topic',
      'primarytopic': 'Primary Topic',
      'Intent': 'Intent',
      'intent': 'Intent',
      'Topics': 'Topics',
      'topics': 'Topics',

      // Resolution and outcome
      'CallResolution': 'Call Resolution',
      'callresolution': 'Call Resolution',
      'Outcome': 'Outcome',
      'outcome': 'Outcome',

      // Time and duration
      'Duration': 'Duration',
      'duration': 'Duration',
      'WaitTime': 'Wait Time',
      'wait_time': 'Wait Time',

      // Journey and moments
      'JourneyMoment': 'Journey Moment',
      'journeymoment': 'Journey Moment',

      // Authentication and tier
      'AuthenticationStatus': 'Authentication Status',
      'authenticationstatus': 'Authentication Status',
      'Tier': 'Tier',
      'tier': 'Tier',

      // General mappings
      'Date': 'Date',
      'date': 'Date',
      'Time': 'Time',
      'time': 'Time',
      'Status': 'Status',
      'status': 'Status',
      'Type': 'Type',
      'type': 'Type',
      'Name': 'Name',
      'name': 'Name',
      'Id': 'ID',
      'id': 'ID'
    };

    // Return mapped name or convert camelCase/snake_case to Title Case
    if (fieldMappings[fieldName]) {
      return fieldMappings[fieldName];
    }

    // Convert camelCase to Title Case
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\s+/, '')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  hasAppliedFilters(): boolean {
    const hasTopLevel = !!(this.chartData?.filters && Array.isArray(this.chartData.filters) && this.chartData.filters.length > 0);
    const hasConfigFilters = !!(this.chartData?.config?.filters && Array.isArray(this.chartData.config.filters) && this.chartData.config.filters.length > 0);
    return hasTopLevel || hasConfigFilters;
  }
}
